import NameCode from '@/component/name-code';
import useWindowSize from '@/hooks/use-window-size';
import {HoldState, RecommendState} from '@/model/const';
import PageData from '@/types/page-data';
import PageParam from '@/types/page-param';
import AppUtil from '@/util/app-util';
import HoldUtil from '@/util/hold-util';
import LabelUtil from '@/util/label-util';
import Utils from '@/util/utils';
import {InfoCircleOutlined, MinusSquareOutlined, PlusSquareOutlined} from '@ant-design/icons';
import {Pagination, Tooltip} from 'antd';
import React from 'react';
import {ReportData} from '../types';
import './report-card.scss';

interface ReportCardPorps {
    data?: PageData<ReportData>;
    expandedRowKeys?: {recommend: number[]; hold: number[]};
    onRowExpand?: (key: 'recommend' | 'hold', expanded: boolean, record: ReportData) => void;
    onStarClick?: (record: ReportData, star: number) => void;
    setPageParam?: (param: PageParam) => void;
}

const ReportCard: React.FC<ReportCardPorps> = (props) => {
    const {data, expandedRowKeys, onRowExpand, onStarClick, setPageParam} = props;
    const {width} = useWindowSize();
    const descWidth = width - 76;
    const reasonWidth = width - 84;

    const getTextTooltip = (text?: string) => {
        if (!text) return '';
        return (
            <div className="tip-text-wrap">
                <Tooltip title={<div className="tip-text">{text}</div>}>
                    <div className="content">{text}</div>
                </Tooltip>
            </div>
        );
    };

    const renderContentCard = (record: ReportData) => {
        const getRecommendState = () => {
            let state = LabelUtil.getRecommendState(record.recommendState);
            if (!state && Utils.isArrayHasData(record.recommend)) {
                state = '--';
            }
            return state;
        };

        const getRecommendInfo = () => {
            const state = getRecommendState();
            if (record.recommendState !== RecommendState.BUY) {
                return <span className="font-bold text-slate-600">{state}</span>;
            }

            let times = <></>;
            let price1 = <></>;
            let price2 = <></>;
            if (record.recommend && record.recommend.length > 0) {
                if (record.recommend.length > 1) {
                    times = (
                        <>
                            <span> </span>
                            <span className="font-black text-blue-800">{record.recommend.length}</span>
                            <span>次</span>
                        </>
                    );
                }
                const r = record.recommend[0];
                price1 = (
                    <>
                        <span>{' 推荐'}</span>
                        <span className="font-bold text-blue-500">{Utils.formatPrice(r.recommendBuyPrice)}</span>
                    </>
                );
                price2 = (
                    <>
                        <span>{' 保本'}</span>
                        <span className="font-bold text-blue-500">{Utils.formatPrice(r.breakevenPrice)}</span>
                    </>
                );
            }
            return (
                <>
                    <span className="font-bold text-blue-600">{state}</span>
                    {times}
                    {price1}
                    {price2}
                </>
            );
        };

        const getHoldInfo = () => {
            const state = getHoldState();
            const bought = record.holdState === HoldState.BOUGHT;
            let buy = <></>;
            let sell = <></>;
            const getNumStyle = (num: number) => {
                return `${num > 1 ? 'font-black' : 'font-bold'} text-green-600`;
            };
            if (record.hold && record.hold.length > 0) {
                buy = (
                    <>
                        <span>{' 买入'}</span>
                        <span className={getNumStyle(record.hold.length)}>{record.hold.length}</span>
                        <span>次</span>
                    </>
                );
                const sold = record.hold.filter((i) => i.holdState === HoldState.SOLD);
                if (sold.length > 0) {
                    sell = (
                        <>
                            <span>{' 卖出'}</span>
                            <span className={getNumStyle(sold.length)}>{sold.length}</span>
                            <span>次</span>
                        </>
                    );
                }
            }
            return (
                <>
                    <span className={`font-bold ${bought ? 'text-green-600' : 'text-slate-600'}`}>{state}</span>
                    {buy}
                    {sell}
                </>
            );
        };

        const getHoldState = () => {
            let state = LabelUtil.getHoldState(record.holdState);
            if (!state && Utils.isArrayHasData(record.hold)) {
                state = '--';
            }
            return state;
        };

        const getLatestPrice = () => {
            const text = record.latestPrice;
            const price = Utils.formatPrice(text !== -1 ? text : undefined);
            const _price = () => {
                return <div className="latest-price">{price}</div>;
            };
            if (text <= 0) return _price();
            if (record.holdState !== HoldState.BOUGHT) return _price();
            if (!Utils.isArrayHasData(record.hold)) return _price();
            const holds = record.hold?.filter((item) => item.holdState === HoldState.BOUGHT) || [];
            const hold = holds.length > 1 ? holds.reduce((a, b) => (a.buyPrice < b.buyPrice ? a : b)) : holds[0];
            if (!hold) return _price();
            const rate = ((text - hold.buyPrice) / hold.buyPrice) * 100;
            return (
                <div className="latest-price-wrap">
                    <div className="latest-price">{price}</div>
                    <span className={`latest-price-rate ${rate < 0 ? 'negative' : 'positive'}`}>{Utils.formatPercentage(rate)}</span>
                </div>
            );
        };

        const getBreakevenPrice = () => {
            const text = record.breakevenPrice;
            return (
                <div className="breakeven-price">
                    {Number(text) > 0 ? Utils.formatPrice(text) : '--'}
                    {record.nearbyInterestDate && (
                        <Tooltip title={`近期付息：${Utils.formatDate(record.nearbyInterestDate)}`}>
                            <InfoCircleOutlined className="info" />
                        </Tooltip>
                    )}
                </div>
            );
        };

        const handleExpandClick = (key: 'recommend' | 'hold', expand: boolean) => {
            onRowExpand?.(key, expand, record);
        };

        return (
            <div className="card-content">
                <div className="flex">
                    <NameCode
                        name={record.name}
                        code={record.code}
                        color={record.tagColor}
                        expireTime={record.expireTime}
                        star={record.tagStar}
                        onStarClick={(star) => onStarClick?.(record, star)}
                    />
                    <div className="industry">{record.industry || ''}</div>
                </div>
                <div className="flex" style={{marginTop: '5px'}}>
                    <div style={{display: 'flex', flex: 1}}>
                        {`现价：`}
                        {getLatestPrice()}
                    </div>
                    <div style={{display: 'flex', flex: 1}}>
                        {`保本价：`}
                        {getBreakevenPrice()}
                    </div>
                </div>
                <div className="flex" style={{marginTop: '5px'}}>
                    <div style={{flex: 1}}>
                        {'推荐信息：'}
                        {getRecommendInfo()}
                    </div>
                    {record.recommend && record.recommend.length > 0 && (
                        <div className="expand-icon">
                            {(expandedRowKeys?.recommend || []).includes(record.id) ? (
                                <MinusSquareOutlined onClick={() => handleExpandClick('recommend', false)} />
                            ) : (
                                <PlusSquareOutlined onClick={() => handleExpandClick('recommend', true)} />
                            )}
                        </div>
                    )}
                </div>
                {!!getHoldState() && (
                    <div className="flex" style={{marginTop: '5px'}}>
                        <div style={{flex: 1}}>
                            <span>{'持有信息：'}</span>
                            {getHoldInfo()}
                        </div>
                        {record.hold && record.hold.length > 0 && (
                            <div className="expand-icon">
                                {(expandedRowKeys?.hold || []).includes(record.id) ? (
                                    <MinusSquareOutlined onClick={() => handleExpandClick('hold', false)} />
                                ) : (
                                    <PlusSquareOutlined onClick={() => handleExpandClick('hold', true)} />
                                )}
                            </div>
                        )}
                    </div>
                )}
                {!!record.description && (
                    <div className="flex" style={{marginTop: '5px'}}>
                        <div style={{display: 'flex', flex: 1, alignItems: 'center'}}>
                            <div style={{whiteSpace: 'nowrap'}}>描述：</div>
                            <div style={{width: `${descWidth}px`}}>{getTextTooltip(record.description)}</div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderRecommendCard = (record: ReportData) => {
        const expanded = (expandedRowKeys?.recommend || []).includes(record.id);
        if (!expanded) return null;
        if (!Utils.isArrayHasData(record.recommend)) return null;

        const getRecommendShortState = (state: string) => {
            let label;
            let color;
            if (state === RecommendState.BUY) {
                label = '买';
                color = '#0066ff';
            } else if (state === RecommendState.SELL) {
                label = '卖';
                color = '#666666';
            } else {
                return undefined;
            }

            return (
                <div className="state-label" style={{color, borderColor: color}}>
                    {label}
                </div>
            );
        };

        return (
            <div className="expand-card-wrap">
                {(record.recommend || []).map((item) => (
                    <div className="expand-card-content card-recommend" key={item.id}>
                        <div className="card-type-label" style={{color: '#0066ff'}}>
                            荐
                        </div>
                        {getRecommendShortState(item.recommendState)}
                        <div className="flex row">
                            <div style={{flex: 1}}>{`买入价：${Utils.formatPrice(item.recommendBuyPrice)}`}</div>
                            <div style={{flex: 1}}>{`保本价：${Utils.formatPrice(item.breakevenPrice)}`}</div>
                        </div>
                        <div className="flex row">
                            <div style={{flex: 1}}>{`溢价率：${Utils.formatPercentage(item.premiumRate)}`}</div>
                            <div style={{flex: 1}}>{`年限：${item.remainYear || 0}`}</div>
                        </div>
                        <div className="flex row">
                            <div style={{flex: 1}}>{`规模：${`${item.residualScale || 0}亿元`}`}</div>
                            <div style={{flex: 1}}>{`总市值：${`${item.totalMarketValue || 0}亿元`}`}</div>
                        </div>
                        <div className="flex row">
                            <div style={{flex: 1}}>{`到期收益：${Utils.formatPercentage(item.yieldToMaturity)}`}</div>
                            <div style={{flex: 1}}>{`卖出收益：${Utils.formatPercentage(item.profitRate)}`}</div>
                        </div>
                        <div className="flex row">
                            <div style={{flex: 1}}>{`买时间：${Utils.formatDate(item.recommendBuyTime)}`}</div>
                            <div style={{flex: 1}}>{`卖时间：${Utils.formatDate(item.recommendSellTime)}`}</div>
                        </div>
                        {item.recommendBuyReason && (
                            <div className="flex row">
                                <div style={{whiteSpace: 'nowrap'}}>买入：</div>
                                <div style={{width: `${reasonWidth}px`}}>{getTextTooltip(item.recommendBuyReason)}</div>
                            </div>
                        )}
                        {item.recommendSellReason && (
                            <div className="flex row">
                                <div style={{whiteSpace: 'nowrap'}}>卖出：</div>
                                <div style={{width: `${reasonWidth}px`}}>{getTextTooltip(item.recommendSellReason)}</div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        );
    };

    const renderHoldCard = (record: ReportData) => {
        const expanded = (expandedRowKeys?.hold || []).includes(record.id);
        if (!expanded) return null;
        if (!Utils.isArrayHasData(record.hold)) return null;

        const getHoldShortState = (state: string) => {
            let label;
            let color;
            if (state === HoldState.BOUGHT) {
                label = '买';
                color = '#32cd32';
            } else if (state === HoldState.SOLD) {
                label = '卖';
                color = '#ff0000';
            } else {
                return undefined;
            }

            return (
                <div className="state-label" style={{color, borderColor: color}}>
                    {label}
                </div>
            );
        };

        return (
            <div className="expand-card-wrap">
                {(record.hold || []).map((item) => (
                    <div className="expand-card-content card-hold" key={item.id}>
                        <div className="card-type-label" style={{color: '#32cd32'}}>
                            持
                        </div>
                        {getHoldShortState(item.holdState)}
                        <div className="flex row">
                            <div style={{flex: 1}}>{`数量：${item.quantity || 0}`}</div>
                        </div>
                        <div className="flex row">
                            <div style={{flex: 1}}>{`买时间：${Utils.formatDate(item.buyTime)}`}</div>
                            <div style={{flex: 1}}>{`买价格：${Utils.formatPrice(item.buyPrice)}`}</div>
                        </div>
                        <div className="flex row">
                            <div style={{flex: 1}}>{`卖时间：${item.holdState === HoldState.SOLD ? Utils.formatDate(item.sellTime) : '--'}`}</div>
                            <div style={{flex: 1}}>{`卖价格：${item.holdState === HoldState.SOLD ? Utils.formatPrice(item.sellPrice) : '--'}`}</div>
                        </div>
                        <div className="flex row">
                            <div style={{flex: 1}}>{`收益：${HoldUtil.getProfit(item, record)}`}</div>
                            <div style={{flex: 1}}>{`收益率：${HoldUtil.getProfitRate(item, record)}`}</div>
                        </div>
                        {item.buyDescription && (
                            <div className="flex row">
                                <div style={{whiteSpace: 'nowrap'}}>买入：</div>
                                <div style={{width: `${reasonWidth}px`}}>{getTextTooltip(item.buyDescription)}</div>
                            </div>
                        )}
                        {item.sellDescription && (
                            <div className="flex row">
                                <div style={{whiteSpace: 'nowrap'}}>卖出：</div>
                                <div style={{width: `${reasonWidth}px`}}>{getTextTooltip(item.sellDescription)}</div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="report-card">
            {(data?.content || []).map((item) => {
                return (
                    <div key={item.id} className="card-wrap">
                        {renderContentCard(item)}
                        {renderRecommendCard(item)}
                        {renderHoldCard(item)}
                    </div>
                );
            })}
            {data && (
                <div className="pagination">
                    <Pagination
                        current={data.page + 1}
                        pageSize={data.size}
                        total={data.total}
                        hideOnSinglePage
                        showSizeChanger
                        onChange={(page, pageSize) => {
                            setPageParam?.({page: page - 1, size: pageSize});
                            AppUtil.scrollToTop();
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default ReportCard;
